import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { api } from "@/lib/api";
import { Building2, LogOut, LayoutDashboard, ShieldCheck, Globe } from "lucide-react";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { t, lang, setLang, languages } = useI18n();
  const isLogged = user && user !== false;

  useEffect(() => {
    if (!isLogged) return;
    api.post("/presence/me").catch(() => {});
    const int = setInterval(() => api.post("/presence/me").catch(() => {}), 30000);
    return () => clearInterval(int);
  }, [isLogged]);

  return (
    <nav className="glass-nav fixed top-0 left-0 right-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-2">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 shrink-0">
          <span className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-white" />
          </span>
          <span className="font-heading font-bold text-xl tracking-tight text-white">Digisídlo</span>
        </Link>
        <div className="flex items-center gap-1.5 sm:gap-3">
          <div className="relative flex items-center">
            <Globe className="w-3.5 h-3.5 text-slate-500 absolute left-2.5 pointer-events-none" />
            <select data-testid="nav-lang-select" value={lang} onChange={(e) => setLang(e.target.value)}
              className="appearance-none pl-8 pr-3 py-1.5 rounded-full bg-transparent border border-slate-700 text-xs font-semibold text-slate-300 hover:border-indigo-500 focus:outline-none cursor-pointer uppercase">
              {languages.map((l) => <option key={l.code} value={l.code} className="bg-[#131B2A]">{l.code.toUpperCase()}</option>)}
            </select>
          </div>
          {isLogged ? (
            <>
              {user.role === "admin" && (
                <Link to="/admin" data-testid="nav-admin-link"
                  className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-slate-200 hover:bg-white/10 transition-colors">
                  <ShieldCheck className="w-4 h-4" /> {t("nav.admin")}
                </Link>
              )}
              <Link to="/dashboard" data-testid="nav-dashboard-link"
                className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full text-sm font-semibold text-slate-200 hover:bg-white/10 transition-colors">
                <LayoutDashboard className="w-4 h-4" /> <span className="hidden sm:inline">{t("nav.overview")}</span>
              </Link>
              <button onClick={logout} data-testid="nav-logout-button"
                className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full text-sm font-semibold text-slate-400 hover:text-white hover:bg-white/10 transition-colors">
                <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">{t("nav.logout")}</span>
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login-button"
                className="px-4 sm:px-5 py-2 rounded-full text-sm font-semibold text-slate-200 hover:bg-white/10 transition-colors">
                {t("nav.login")}
              </Link>
              <Link to="/register" data-testid="nav-register-button"
                className="px-4 sm:px-5 py-2 rounded-full text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-500 transition-colors">
                {t("nav.register")}
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};
